import { useState, useRef, useEffect } from 'react';

interface Recipient {
  id: string; // Added ID field
  full_name: string;
  email: string;
  company?: string; // Optional field
}

interface MultiSelectDropdownProps {
  recipients: Recipient[];
  selected: Recipient[];
  onSelect: (selected: Recipient[]) => void;
  placeholder?: string;
}

export function MultiSelectDropdown({
  recipients,
  selected,
  onSelect,
  placeholder = "Select recipients..."
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredRecipients = recipients?.filter(recipient =>
    recipient?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recipient?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleRecipient = (recipient: Recipient) => {
    const isSelected = selected.some(r => r.id === recipient.id);
    if (isSelected) {
      onSelect(selected.filter(r => r.id !== recipient.id));
    } else {
      onSelect([...selected, recipient]);
    }
  };

  const removeRecipient = (id: string) => {
    onSelect(selected.filter(r => r.id !== id));
  };

  return (
    <div className="mb-4 relative" ref={dropdownRef}>
      <label className="block text-sm font-medium mb-1">Recipients</label>
      
      {/* Selected recipients display */}
      <div 
        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer min-h-[42px] flex flex-wrap gap-2"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selected.length === 0 ? (
          <span className="text-gray-400">{placeholder}</span>
        ) : (
          selected.map(recipient => (
            <span 
              key={recipient.id} 
              className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded flex items-center gap-1"
            >
              {recipient.full_name}
              <button 
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeRecipient(recipient.id);
                }}
                className="text-blue-500 hover:text-blue-700"
              >
                &times;
              </button>
            </span>
          ))
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 border border-gray-200 max-h-60 overflow-auto">
          {/* Search input */}
          <div className="sticky top-0 bg-white p-2 border-b">
            <input
              type="text"
              placeholder="Search recipients..."
              className="w-full px-2 py-1 text-sm border rounded"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          {/* Recipients list */}
          <ul>
            {filteredRecipients.length > 0 ? (
              filteredRecipients.map(recipient => (
                <li 
                  key={recipient.id}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 flex items-center justify-between ${
                    selected.some(r => r.id === recipient.id) ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => toggleRecipient(recipient)}
                >
                  <div>
                    <div className="font-medium">{recipient.full_name}</div>
                    <div className="text-gray-500 text-xs">
                      {recipient.email}
                      {recipient.company && ` • ${recipient.company}`}
                    </div>
                  </div>
                  {selected.some(r => r.id === recipient.id) && (
                    <span className="text-blue-500">✓</span>
                  )}
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-sm text-gray-500">No recipients found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}